<template>
  <KeepAlive>
    <Modal
      classs
      c-class="page-settings"
      h-class="bg-light1 p-0 page-settings__header"
      m-class="p-0"
      f-class="p-0"
      overflow-hidden
      @cancel="cancel">
      <template #header>
        <div class="d-flex align-items-center page-settings__header-content px-4">
          <p class="heading-h3-20-medium">Publish Settings</p>
          <div class="page-settings__btn" @click.prevent="cancel">
            <i class="icon icon-cancel"></i>
          </div>
        </div>
      </template>
      <template #default class="m-0">
        <div v-if="loadingPageSetting" class="d-flex align-items-center justify-content-center page-settings__loader">
          <GLoading size="large" />
        </div>
        <div v-else class="d-flex flex-column">
          <div class="d-flex justify-content-start">
            <div ref="sideBar" class="page-settings__sidebar">
              <ul class="page-settings__sidebar-nav">
                <li v-for="(section, index) in sections" :key="index" class="page-settings__sidebar-nav-items">
                  <a
                    href="javascript:void(0)"
                    class="page-settings__sidebar-nav-items-link"
                    :class="{ active: activeMenu === section.id }"
                    @click="scrollTo(section.id)">
                    {{ section.text }}
                  </a>
                </li>
              </ul>
            </div>
            <div class="bg-light3 page-settings__scroll grow">
              <div id="page-settings__body" class="page-settings__body pr-4 pt-2">
                <General
                  :is-highlight-g-m-prop="sectionId === 'seo_setting'"
                  :setting-url="settingUrl"
                  @cancel="cancel" />
                <AdvanceSetting :is-highlight-g-m-prop="sectionId === 'advanced_setting'" />
                <LayoutSetting ref="layoutSetting" :style="`margin-bottom: ${layoutSettingMargin}px !important`" />
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div v-if="!loadingPageSetting" class="btn-group page-settings__action d-flex align-items-center p-4">
          <button class="btn btn-outline btn-default ml-auto mr-1 px-4" @click="cancel">Cancel</button>
          <ProcessButton
            v-model:status="btnStatus"
            class="btn-default px-4"
            type="submit"
            default="btn-primary"
            :disabled="disableSave"
            @click="save">
            <template #loading>Applying</template>
            <template #success>Success</template>
            <template #error>Error</template>
            <template #default>Apply Settings</template>
          </ProcessButton>
        </div>
      </template>
    </Modal>
  </KeepAlive>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import CategoryBar from '@modules/libraries/components/tabs/category/CategoryBar.vue';
import General from './General.vue';
import LayoutSetting from './LayoutSetting.vue';
import LazyLoadingUpdatePlan from './LazyLoadingUpdatePlan.vue';
import AdvanceSetting from './AdvanceSetting.vue';
import { mapGetters, mapActions, mapState } from 'vuex';
import initProcessButton from '@/mixins/processButton';
import errorHandle from '@/mixins/errorHandle.js';
import HELPER from '@/app/utils/HELPER';

/* eslint-disable vue/no-unused-components */
export default defineComponent({
  name: 'PageSettingsModal',
  components: {
    CategoryBar,
    General,
    LayoutSetting,
    AdvanceSetting,
    LazyLoadingUpdatePlan,
  },
  mixins: [initProcessButton, errorHandle()],
  props: {
    settingUrl: {
      type: Boolean,
      default: false,
    },
    sectionId: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    sections: [
      {
        text: 'SEO',
        id: 'seo_setting',
      },
      {
        text: 'Social Sharing',
        id: 'social_sharing',
      },
      {
        text: 'Advanced',
        id: 'advanced_setting',
      },
      {
        text: 'Layout',
        id: 'layout_setting',
      },
    ],
    activeMenu: 'seo_setting',
    layoutSettingMargin: 0,
    observer: {} as ResizeObserver,
  }),
  computed: {
    ...mapGetters('page', {
      blankTextInput: 'blankPageSettingText',
    }),
    ...mapState('page', {
      settingChange: 'pageSettingChange',
      loadingPageSetting: 'loadingPageSetting',
      pageSettingIsError: 'pageSettingIsError',
    }),
    ...mapState('app', {
      pageInfo: 'pageInfo',
    }),
    isScheduledPublish() {
      const events = this.pageInfo?.page?.events;
      if (!events.length) return false;
      const scheduleEvent = events.find((x: any) => x.event == 'schedule_publish');
      // eslint-disable-next-line camelcase
      return scheduleEvent?.event_at;
    },
    disableSave() {
      if (this.blankTextInput) return true;
      if (this.pageSettingIsError) return true;

      for (const change of Object.values(this.settingChange)) {
        if (change) return false;
      }
      return true;
    },
  },
  async created() {
    if (this.sectionId) {
      this.activeMenu = this.sectionId || 'seo_setting';
    }
    try {
      await this.getPageSetting();
    } catch (e) {
      HELPER.showNotify('Get page setting error', 'error');
      this.cancel();
    }
    // this.handleScroll();
    this.initObserver();
    this.scrollTo(this.activeMenu);
  },
  unmounted() {
    if (this.observer && this.$refs.sideBar) this.observer.unobserve(this.$refs.sideBar as HTMLElement);
  },
  methods: {
    ...mapActions('page', {
      getPageSetting: 'getPageSetting',
      savePageSetting: 'savePageSetting',
    }),
    onResize() {
      if (this.$refs.sideBar && this.$refs.layoutSetting) {
        const sideBarHeight = (this.$refs.sideBar as HTMLElement).clientHeight;
        const layoutSettingHeight = (this.$refs.layoutSetting as Vue & { $el: HTMLElement }).$el.clientHeight;
        const outerSize = 16;
        this.layoutSettingMargin = sideBarHeight - layoutSettingHeight - outerSize;
      }
    },
    initObserver() {
      const observer = new ResizeObserver(this.onResize);
      this.$refs.sideBar && observer.observe(this.$refs.sideBar as Element);
      this.observer = observer;
    },
    handleScroll() {
      const body = document.getElementById('page-settings__body') as HTMLElement;
      if (body) {
        body.onscroll = () => {
          const scrollY = body.scrollTop;
          const sections = document.querySelectorAll('div[id]');
          sections.forEach((current) => {
            const sectionHeight = (current as HTMLElement).offsetHeight;
            const sectionTop = (current as HTMLElement).offsetTop - 120;
            const sectionId = current.getAttribute('id');
            if (scrollY <= 130) {
              this.activeMenu = 'seo_setting';
            } else if (scrollY > sectionTop && scrollY <= sectionTop + sectionHeight) {
              this.activeMenu = sectionId as string;
            }
          });
        };
      }
    },
    scrollTo(elId: string) {
      this.$nextTick(() => {
        this.activeMenu = '';
        const el = document.getElementById(elId);
        const body = document.getElementById('page-settings__body');
        if (el) {
          // el.scrollIntoView();
          const yOffset = -96;
          const offsetTop = el.offsetTop;
          const y = offsetTop + yOffset;
          body?.scrollTo({ top: y, behavior: 'smooth' });
          this.activeMenu = elId;
          this.$forceUpdate();
        }
      });
    },
    cancel() {
      this.$emit('cancel');
      this.$store.commit('app/DISABLE_HISTORY_ITEM', false);
    },
    addPageToUpdatedList(pageId: any) {
      const listItem = localStorage.getItem('updateSettingList');
      const list = listItem ? JSON.parse(listItem) : [];
      if (!list.includes(pageId)) {
        list.push(pageId);
      }
      if (!this.isScheduledPublish) localStorage.setItem('updateSettingList', JSON.stringify(list));
    },
    async save() {
      if (this.disableSave) return;

      this.activeLoading();
      return this.savePageSetting()
        .then(() => {
          this.activeSuccess();
          const pageId = this.pageInfo.page.g_id;
          this.addPageToUpdatedList(pageId);
          this.$store.commit('page/ADD_SETTING_CHANGED_LIST', pageId);
        })
        .catch((err: any) => {
          // eslint-disable-next-line no-console
          console.error(err);
          this.activeError();
        })
        .finally(() => {
          setTimeout(() => {
            this.resetStatus();
          }, 1000);
        });
    },
  },
});
</script>

<style lang="scss">
@import '@/app/editor/scss/variables';
@import '@/core/themes/vendor/color';
@import '@/core/themes/vendor/variables';
@import '@/core/themes/vendor/mixins';

$screen-md: 848px;
$btn-gap: 9px;
$header-size: 50px;
$height-action: 104px;
$height-scroll: calc(70vh - 20px);

.gem-meter-highlight {
  &__3rd-party {
    position: relative !important;
    border-color: theme('colors.amber.300');
    &:after {
      content: ' ';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      opacity: 0;
      background-color: theme('colors.amber.300');
      animation-name: blink;
      animation-duration: 0.8s;
      animation-iteration-count: 3;
      border-radius: 4px;
    }
  }
}

.page-settings__sidebar {
  width: 155px;
  @media (max-width: 768px) {
    display: none;
  }
  .page-settings__sidebar-nav {
    position: sticky;
    top: 0;
    list-style: none;
    padding-left: 32px;
    padding-top: 16px;
    &-items {
      &-link {
        display: flex;
        align-items: center;
        width: 100%;
        height: 40px;
        font-style: normal;
        font-weight: 500;
        font-size: 14px;
        line-height: 16px;
        color: $black4;
        &:hover,
        &.active {
          color: $mono4;
        }
      }
    }
  }
}

.page-settings {
  width: 60vw;
  max-width: $screen-md;
  border-radius: 3px;

  &__loader {
    width: 100%;
    min-height: $height-scroll;
  }

  &__header {
    border-radius: 3px 3px 0 0;
    &-content {
      justify-content: space-between;
      height: 80px;
      width: 100%;
    }
  }

  &__sidebar {
    min-width: 184px;
  }

  &__btn {
    @include flex-center;
    color: $text-light-low;
    width: $header-size - $btn-gap * 2;
    height: $header-size - $btn-gap * 2;

    font-size: 20px;
    cursor: pointer;
    border-radius: 3px;
    &:hover {
      background: $light2;
    }
  }

  &__scroll {
    height: $height-scroll;
  }

  &__body {
    height: 100%;
    width: 100%;
    max-width: 693px;
    border-radius: 3px;
    overflow: scroll !important;
    overflow-x: hidden !important;
  }

  &__action {
    background-color: $neutral5;
    border-top: solid 1px $text-dark-high;
    border-radius: 0 0 3px 3px;
    width: 100%;
    height: 89px;
  }
}
</style>
